// VENDOR-LIFECYCLE.2 — Retire Vendor inline Dialog
// D-VENDOR-LIFECYCLE.2-PE-1: inline Dialog per CertificationsTab pattern
// VL.1 contract: POST /vendors/:id/retire { reason: string (1-500) }

import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useRetireVendor } from '@/api/hooks/useVendors';

const MIN_CHARS = 1;
const MAX_CHARS = 500;

interface VendorRetireModalProps {
  vendorId: string;
  vendorName: string;
  open: boolean;
  onClose: () => void;
}

export function VendorRetireModal({ vendorId, vendorName, open, onClose }: VendorRetireModalProps) {
  const [reason, setReason] = useState('');
  const [inModalError, setInModalError] = useState<string | null>(null);
  const mutation = useRetireVendor(vendorId);

  const trimmed = reason.trim();
  const canSubmit = trimmed.length >= MIN_CHARS && trimmed.length <= MAX_CHARS && !mutation.isPending;

  function handleClose() {
    if (mutation.isPending) return;
    setReason('');
    setInModalError(null);
    onClose();
  }

  async function handleConfirm() {
    if (!canSubmit) return;
    setInModalError(null);
    try {
      await mutation.mutateAsync({ reason: trimmed });
      handleClose();
    } catch (err: any) {
      const msg = err?.response?.data?.errors?.message ?? err.message ?? 'Failed to retire vendor';
      setInModalError(msg);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Retire Vendor</DialogTitle>
        </DialogHeader>

        <div className="py-2 space-y-4">
          <p className="text-sm text-text-primary">
            Retiring <span className="font-semibold">{vendorName}</span> will change its status to{' '}
            <span className="font-semibold">Offboarded</span>. The vendor can be reactivated later if needed.
          </p>

          <div className="space-y-1.5">
            <Label htmlFor="retire-reason">
              Reason <span className="text-danger">*</span>
            </Label>
            <Textarea
              id="retire-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Describe the reason for retiring this vendor (e.g. contract ended, service discontinued)"
              maxLength={MAX_CHARS}
              rows={4}
            />
            <p className="text-xs text-text-secondary text-right">
              {trimmed.length} / {MAX_CHARS}
            </p>
          </div>

          {inModalError && (
            <p className="text-sm text-danger">{inModalError}</p>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={!canSubmit}>
            {mutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Retire Vendor
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
